/**
 * CarrierHandoff — completion-state tile for endorsement workflows that
 * end with a manual carrier-portal step.
 *
 * Ported from v1 c:/_Data/mcp/src/outlook-addin/src/components/CarrierHandoff.tsx
 * Restyle: every Fluent UI primitive + inline hex replaced with our
 * Tailwind tokens per plan §"Restyle rules".
 */
import CustomTooltip from "@product-app/common/components/CustomTooltip";

export interface CarrierHandoffProps {
  url: string | null;
  instructions: string | null;
  carrierName: string | null;
  hasCarrierConnect: boolean;
}

export function CarrierHandoff({
  url,
  instructions,
  carrierName,
  hasCarrierConnect,
}: CarrierHandoffProps) {
  return (
    <div
      className="mb-4 rounded-md border-2 border-primary-success-600 bg-primary-success-25 overflow-hidden"
      data-testid="carrier-handoff"
    >
      <div className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <svg
            className="w-5 h-5 text-primary-success-700"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
          <span className="text-md font-semibold text-primary-success-700">
            Carrier Portal Ready
          </span>
          {hasCarrierConnect && (
            <span className="ml-auto text-2xs font-semibold px-1.5 py-[1px] rounded bg-sunrise/20 text-primary-warning-700">
              SSO Available
            </span>
          )}
        </div>

        {carrierName && (
          <p className="text-sm font-semibold text-primary-gray-light-900 mb-2">
            {carrierName}
          </p>
        )}

        {url ? (
          // ora's tooltip, not a native title (story 28633). Node title +
          // describeChild: the button keeps its label as its name.
          <CustomTooltip
            id="carrier-handoff-open"
            title={<span>Opens the carrier&apos;s website in a new tab.</span>}
            describeChild
            placement="top"
            arrow
            isOnModal
          >
            <button
              type="button"
              onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
              className="w-full mb-3 px-3 py-2 rounded-md bg-sunrise text-primary-gray-light-800 text-sm font-semibold hover:shadow-[0px_0px_2px_2px_rgba(255,164,0,0.24)] transition-shadow duration-200"
            >
              Open Carrier Portal
            </button>
          </CustomTooltip>
        ) : (
          <div className="mb-3 px-3 py-2 rounded-md bg-primary-warning-50 border border-primary-warning-200">
            <p className="text-sm font-semibold text-primary-warning-700">
              No portal URL available
            </p>
            <p className="text-xs text-primary-warning-700">
              Contact the carrier directly or check Epic for Carrier Connect access.
            </p>
          </div>
        )}

        {instructions && (
          <div>
            <p className="text-xs font-semibold text-primary-gray-light-700 mb-1">
              Instructions
            </p>
            <p className="text-xs text-primary-gray-light-600 whitespace-pre-wrap leading-snug">
              {instructions}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default CarrierHandoff;
